import { message } from 'antd';
import * as HttpStatus from 'http-status-codes';
import { MSG_ERROR } from '../../constants';
import { getAuthSessionFromResponse } from '../../components/Login/LoginAction';
import resolvePostLoginPath from '../../components/Login/resolvePostLoginPath';
import { getApiErrorMessage, getApiResponseMessage, parseHttpError } from '../../utils/httpError';
import {
  mergeRegistrationActivation,
  ONBOARDING_OTP_RESEND_COOLDOWN_MS,
  requiresOtpVerificationAfterSubmit,
} from './onboardingRegistrationHelpers';
import {
  parseOrgUserAcceptResponse,
  resendOnboardingRegistrationOtp,
  submitOnboardingRegistration,
  validateOrgUserInvitation,
  verifyActivationCredentials,
  verifyOnboardingAccountEmail,
  verifyOnboardingRegistrationOtp,
} from './onboardingApi';

async function pushResolvedPostLoginPath({ history, fetchBroker, fetchOrganisations }) {
  const redirectPath = await resolvePostLoginPath({ fetchBroker, fetchOrganisations });
  history.push(redirectPath);
}
const EMAIL_ALREADY_REGISTERED_FALLBACK =
  'This email is already registered. Please log in with your existing account.';

function isAlreadyRegisteredEmailError(error) {
  const { status, message, code } = parseHttpError(error);
  const token = String(code || message || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

  if (status === HttpStatus.BAD_REQUEST || status === HttpStatus.CONFLICT) {
    return true;
  }

  return (
    token.includes('alreadyregistered') ||
    token.includes('emailalreadyexists') ||
    token.includes('useralreadyexists') ||
    token.includes('accountexists') ||
    String(message || '')
      .toLowerCase()
      .includes('already registered')
  );
}

export async function runAccountEmailVerification(email) {
  const normalizedEmail = String(email ?? '').trim();
  if (!normalizedEmail) {
    return { ok: false, error: 'Please enter an email address.' };
  }

  try {
    const response = await verifyOnboardingAccountEmail(normalizedEmail);
    return {
      ok: true,
      message: getApiResponseMessage(response?.data, 'Email verified.'),
    };
  } catch (err) {
    const alreadyRegistered = isAlreadyRegisteredEmailError(err);

    return {
      ok: false,
      alreadyRegistered,
      error: alreadyRegistered
        ? getApiErrorMessage(err, EMAIL_ALREADY_REGISTERED_FALLBACK)
        : getApiErrorMessage(err, 'We could not verify this email address.'),
    };
  }
}

export async function runOrgUserInvitationValidation(invitationId, userType) {
  try {
    const response = await validateOrgUserInvitation(invitationId, userType);
    return { ok: true, data: response?.data ?? null };
  } catch (err) {
    return {
      ok: false,
      data: null,
      error: getApiErrorMessage(err, 'This invitation is invalid or has expired.'),
    };
  }
}

export async function runActivationVerification(activation = {}) {
  try {
    const response = await verifyActivationCredentials(activation);
    return {
      ok: true,
      message: getApiResponseMessage(
        response?.data,
        'Activation verified. You can continue with onboarding.',
      ),
    };
  } catch (err) {
    return {
      ok: false,
      error: getApiErrorMessage(err, 'This subscription key or promo code is invalid.'),
    };
  }
}

export async function runPreRegistrationActivation(activation) {
  try {
    const response = await verifyActivationCredentials(activation);
    return {
      ok: true,
      message: getApiResponseMessage(response?.data, 'You can continue with onboarding.'),
      activation,
    };
  } catch (err) {
    return {
      ok: false,
      error: getApiErrorMessage(err, 'This subscription key or promo code is invalid.'),
    };
  }
}

export async function runOtpVerification({
  userId,
  otpCode,
  completeAuthenticatedSession,
  history,
  fetchBroker,
  fetchOrganisations,
}) {
  const normalizedUserId = String(userId ?? '').trim();
  const normalizedCode = String(otpCode ?? '').trim();

  if (!normalizedUserId || !normalizedCode) {
    return { ok: false, error: 'Enter the verification code from your email.' };
  }

  try {
    const response = await verifyOnboardingRegistrationOtp({
      userId: normalizedUserId,
      otpCode: normalizedCode,
    });
    await completeAuthenticatedSession(response);
    message.success('Email verified. Welcome!');
    await pushResolvedPostLoginPath({ history, fetchBroker, fetchOrganisations });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: getApiErrorMessage(
        err,
        'The code is invalid or has expired. Try again or resend a new code.',
      ),
    };
  }
}

export async function runOtpResend({ email, password }) {
  const normalizedEmail = String(email ?? '').trim();

  if (!normalizedEmail || !password) {
    return { ok: false, error: 'We could not resend the code. Please try registering again.' };
  }

  try {
    await resendOnboardingRegistrationOtp({ email, password });
    return {
      ok: true,
      message: 'A new verification code has been sent to your email.',
      resendAvailableAt: Date.now() + ONBOARDING_OTP_RESEND_COOLDOWN_MS,
    };
  } catch (err) {
    return {
      ok: false,
      error: getApiErrorMessage(err, 'We could not resend the code. Please try again.'),
    };
  }
}

export async function submitRegistrationAndSignIn({
  registration,
  linkParams,
  activationValues,
  completeAuthenticatedSession,
  login,
  history,
  fetchBroker,
  fetchOrganisations,
}) {
  const activation = mergeRegistrationActivation(registration, linkParams, activationValues);
  const nextRegistration = {
    ...registration,
    activation,
  };
  const needsOtpVerification = requiresOtpVerificationAfterSubmit(nextRegistration, linkParams);

  try {
    const response = await submitOnboardingRegistration(nextRegistration, linkParams);
    const session = getAuthSessionFromResponse(response);

    if (session) {
      try {
        await completeAuthenticatedSession(response);
        message.success('Registration complete. Welcome!');
        await pushResolvedPostLoginPath({ history, fetchBroker, fetchOrganisations });
        return { ok: true, registration: nextRegistration };
      } catch {
        const email = String(nextRegistration.account?.email ?? '').trim();
        const password = nextRegistration.account?.password ?? '';

        try {
          await login({ email, password });
          message.success('Registration complete. Welcome!');
          await pushResolvedPostLoginPath({ history, fetchBroker, fetchOrganisations });
          return { ok: true, registration: nextRegistration };
        } catch (loginErr) {
          message.warning(
            getApiErrorMessage(
              loginErr,
              'Registration complete. Please log in with your email and password.',
            ),
          );
          history.push('/');
          return { ok: true, registration: nextRegistration, redirectedToLogin: true };
        }
      }
    }

    if (needsOtpVerification) {
      const { userId, message: acceptMessage } = parseOrgUserAcceptResponse(response);
      const email = String(nextRegistration.account?.email ?? '').trim();

      if (!userId) {
        message.error(
          'Registration succeeded but we could not start email verification. Please log in to continue.',
        );
        history.push('/');
        return { ok: false };
      }

      message.success(
        getApiResponseMessage(
          response?.data,
          acceptMessage || 'We sent a verification code to your email.',
        ),
      );

      return {
        ok: true,
        registration: nextRegistration,
        pendingOtpVerification: true,
        otpUserId: userId,
        email,
        otpResendAvailableAt: Date.now() + ONBOARDING_OTP_RESEND_COOLDOWN_MS,
      };
    }

    message.error('Registration completed but we could not sign you in. Please log in to continue.');
    history.push('/');
    return { ok: false };
  } catch (err) {
    message.error(getApiErrorMessage(err, MSG_ERROR));
    return { ok: false };
  }
}
